import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Phone, Mail, Shield, TrendingUp, CheckCircle, CreditCard, Clock, X, Play, Image as ImageIcon, Video, Upload, Trash2, LogOut, Menu, Star, AlertTriangle, DollarSign, Eye, HelpCircle, Users, MessageCircle, Send, UserPlus, Link as LinkIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ SEO COMPONENT ============
const SEO = ({ title, description, keywords, canonicalPath = "" }) => {
  const baseUrl = window.location.origin;
  const fullTitle = title ? `${title} | TRB Credit King` : "TRB Credit King | Premium Tradelines & Primary Accounts";
  const fullDescription = description || "Premium authorized user tradelines and primary accounts to boost your credit score. Navy Federal, Discover, Amex tradelines available. Call 410-863-2396.";
  const fullKeywords = keywords || "tradelines, authorized user tradelines, primary accounts, credit building, boost credit score, Navy Federal tradelines, credit repair";
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={fullKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
    </Helmet>
  );
};

// ============ IMAGE ZOOM MODAL ============
const ImageZoomModal = ({ src, onClose }) => (
  <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <button onClick={onClose} className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full">
      <X size={32} />
    </button>
    <div onClick={e => e.stopPropagation()} className="max-w-4xl max-h-[90vh]">
      {src.toLowerCase().includes('.mov') || src.toLowerCase().includes('.mp4') || src.toLowerCase().includes('video') ? (
        <video src={src} controls className="max-w-full max-h-[90vh]" autoPlay />
      ) : (
        <img src={src} alt="Zoomed" className="max-w-full max-h-[90vh] object-contain" />
      )}
    </div>
  </div>
);

// ============ NAVBAR ============
const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <nav className="bg-slate-900 text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-emerald-400" />
            <span className="text-2xl font-bold">TRB <span className="text-emerald-400">Tradelines</span></span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="hover:text-emerald-400 transition">Home</Link>
            <Link to="/tradelines" className="hover:text-emerald-400 transition">Tradelines</Link>
            <Link to="/become-seller" className="hover:text-emerald-400 transition">Become a Seller</Link>
            <Link to="/affiliates" className="hover:text-emerald-400 transition">Affiliates</Link>
            <Link to="/help" className="hover:text-emerald-400 transition">Help</Link>
            <a href="tel:410-863-2396" className="flex items-center gap-2 border-2 border-emerald-400 text-white px-4 py-2 rounded-lg hover:bg-emerald-400/10 transition">
              <Phone size={18} /> 410-863-2396
            </a>
          </div>
          
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Menu size={24} />
          </button>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 space-y-3 pb-4">
            <Link to="/" className="block hover:text-emerald-400">Home</Link>
            <Link to="/tradelines" className="block hover:text-emerald-400">Tradelines</Link>
            <Link to="/become-seller" className="block hover:text-emerald-400">Become a Seller</Link>
            <Link to="/affiliates" className="block hover:text-emerald-400">Affiliates</Link>
            <Link to="/help" className="block hover:text-emerald-400">Help</Link>
            <a href="tel:410-863-2396" className="flex items-center gap-2 text-emerald-400">
              <Phone size={18} /> 410-863-2396
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============ FOOTER ============
const Footer = () => (
  <footer className="bg-slate-900 text-white py-8">
    <div className="max-w-7xl mx-auto px-4 text-center">
      <p className="text-gray-400">© 2025 TRB Tradelines. All rights reserved.</p>
      <div className="mt-4 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
        <a href="mailto:trb.credit.king@gmail.com" className="flex items-center justify-center gap-2 text-emerald-400 hover:text-emerald-300">
          <Mail size={18} /> trb.credit.king@gmail.com
        </a>
        <a href="tel:410-863-2396" className="flex items-center justify-center gap-2 text-emerald-400 hover:text-emerald-300">
          <Phone size={18} /> 410-863-2396
        </a>
      </div>
    </div>
  </footer>
);

// ============ HOME PAGE ============
const Home = () => {
  const [tradelines, setTradelines] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        const [tradelinesRes, contentRes] = await Promise.all([
          axios.get(`${API}/tradelines`),
          axios.get(`${API}/content`).catch(() => ({ data: null }))
        ]);
        setTradelines(tradelinesRes.data.filter(t => t.featured));
        setContent(contentRes.data);
        
        // Track page view
        axios.post(`${API}/track-view`, { page: 'home' }).catch(() => {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Default content values
  const heroTitle = content?.heroTitle || "TRB Tradelines";
  const heroSubtitle = content?.heroSubtitle || "Your Trusted Partner in Building Credit Excellence";
  const heroDescription = content?.heroDescription || "Premium authorized user tradelines to help you achieve your credit goals. Fast, secure, and reliable service.";
  const aboutTitle = content?.aboutTitle || "Why Choose TRB Tradelines?";
  const secureTitle = content?.secureTitle || "Secure & Protected";
  const secureDesc = content?.secureDesc || "Your personal information is encrypted and handled with the highest security standards. Your SSN is protected and secure.";
  const resultsTitle = content?.resultsTitle || "Proven Results";
  const resultsDesc = content?.resultsDesc || "High-quality tradelines with excellent payment history and low utilization to help boost your credit profile.";
  const transparencyTitle = content?.transparencyTitle || "Full Transparency";
  const transparencyDesc = content?.transparencyDesc || "We screenshot the entire process and keep you updated via email. You'll know exactly what's happening every step of the way.";
  const contactPhone = content?.contactPhone || "410-863-2396";

  return (
    <div>
      <SEO 
        title="Premium Tradelines & Primary Accounts"
        description="TRB Credit King offers premium authorized user tradelines and primary accounts to boost your credit score. Navy Federal, Discover, Amex tradelines with verified proof. Trusted credit building service. Call 410-863-2396."
        keywords="tradelines, buy tradelines, authorized user tradelines, primary accounts, credit building, boost credit score, Navy Federal tradelines, Discover tradelines, Amex tradelines, credit repair, AU tradelines, Baltimore tradelines, TRB Credit King"
        canonicalPath="/"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 px-4">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHptLTQgMTZjLTIuMjEgMC00IDEuNzktNCA0czEuNzkgNCA0IDQgNC0xLjc5IDQtNC0xLjc5LTQtNC00eiIvPjwvZz48L2c+PC9zdmc+\")"}}></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-12 h-12 text-emerald-400" />
              <h1 className="text-5xl md:text-6xl font-bold">{heroTitle.includes('TRB') ? <>TRB <span className="text-emerald-400">Tradelines</span></> : heroTitle}</h1>
            </div>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">{heroSubtitle}</p>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">{heroDescription}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link to="/tradelines" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2">
                <CreditCard size={20} /> Browse Tradelines
              </Link>
              <a href={`tel:${contactPhone}`} className="border-2 border-emerald-400 text-white hover:bg-emerald-400/10 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                <Phone size={20} /> {contactPhone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-900">{aboutTitle}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow border-2 border-slate-200 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl p-6">
              <Shield className="w-12 h-12 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{secureTitle}</h3>
              <p className="text-slate-600">{secureDesc}</p>
            </div>
            <div className="bg-white rounded-xl shadow border-2 border-slate-200 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl p-6">
              <TrendingUp className="w-12 h-12 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{resultsTitle}</h3>
              <p className="text-slate-600">{resultsDesc}</p>
            </div>
            <div className="bg-white rounded-xl shadow border-2 border-slate-200 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl p-6">
              <CheckCircle className="w-12 h-12 text-emerald-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{transparencyTitle}</h3>
              <p className="text-slate-600">{transparencyDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tradelines */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-4 text-slate-900">Featured Tradelines</h2>
          <p className="text-center text-slate-600 mb-12 text-lg">Select from our premium tradeline offerings</p>
          
          {loading ? (
            <div className="text-center py-12">Loading tradelines...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tradelines.slice(0, 4).map(tradeline => (
                <TradelineCard key={tradeline._id} tradeline={tradeline} />
              ))}
            </div>
          )}
          
          <div className="text-center mt-12">
            <Link to="/tradelines" className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 px-8 py-3 rounded-lg font-medium transition">
              View All Tradelines
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-900">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Browse", desc: "Select the tradeline that fits your needs and credit goals" },
              { step: 2, title: "Checkout", desc: "Securely submit your information with our encrypted form" },
              { step: 3, title: "Pay", desc: "Complete payment via Zelle or Cash App, then text us" },
              { step: 4, title: "Relax", desc: "We handle the rest and keep you updated via email" }
            ].map(item => (
              <div key={item.step} className="text-center space-y-4">
                <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclosure & Info Cards */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl space-y-8">
          {/* Tradeline Disclosure */}
          <div className="rounded-xl shadow border-2 border-amber-200 bg-amber-50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
              <h3 className="text-2xl font-semibold text-slate-900">Tradeline Disclosure & Terms</h3>
            </div>
            <ul className="space-y-3">
              {[
                "It takes 30 to 45 days for the tradeline to post, sometimes a little more.",
                "I screenshot the whole process to show that you're added.",
                "You will be getting an email confirmation.",
                "This is a waiting process. It's like purchasing a real credit card and waiting for that credit card to post.",
                "The shipping address will be used as the tradeline address for your credit."
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What You Need */}
          <div className="rounded-xl shadow border-2 border-slate-300 bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">📋📋 What You Need</h3>
            <ul className="space-y-4">
              <li className="text-lg text-slate-800 font-medium bg-white p-4 rounded-lg shadow-sm border border-slate-200">✓ NO documentation needed to be added</li>
              <li className="text-lg text-slate-800 font-medium bg-white p-4 rounded-lg shadow-sm border border-slate-200">✓ Credit profile requires: SSN card</li>
              <li className="text-lg text-slate-800 font-medium bg-white p-4 rounded-lg shadow-sm border border-slate-200">✓ 24/7 Text Support - 410-863-2396</li>
            </ul>
          </div>

          {/* Primary Accounts */}
          <div className="rounded-xl shadow border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 p-6">
            <h3 className="text-2xl font-semibold text-slate-900 mb-2">⭐⭐ PRIMARY ACCOUNTS (Co-Borrower)</h3>
            <p className="text-lg font-bold text-purple-800 mb-4">Navy Fed Loans - Permanent Credit Boost</p>
            <ul className="space-y-3">
              {[
                "These are PRIMARY accounts - NOT authorized user tradelines",
                "You will be added as CO-BORROWER on the loan",
                "ONE-TIME FLAT FEE - No monthly charges ever",
                "Will NOT fall off your credit - stays permanently",
                "Shows as a primary account on your credit report",
                "Much stronger impact than authorized user tradelines"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Authorized User Tradelines */}
          <div className="rounded-xl shadow border-2 border-blue-200 bg-blue-50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-semibold text-slate-900">💳 Authorized User Tradelines</h3>
            </div>
            <p className="text-lg font-semibold text-blue-900 mb-4">Monthly Maintenance Fee - $75/month after first payment</p>
            <ul className="space-y-3">
              {[
                "$75/month fee applies AFTER FIRST PAYMENT",
                "Payment due using same method (Zelle or Cash App)",
                "⚠️ IMPORTANT: Account will close after 2 warning calls if monthly fee is not paid",
                "Text any questions first to 410-863-2396, then we will give you a call",
                "$7 CashApp processing fee applies to all CashApp payments",
                "These are authorized user accounts and will eventually fall off"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Lowest Prices */}
          <div className="rounded-xl shadow border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
            <h3 className="text-3xl font-semibold text-emerald-900 mb-2">💰 Lowest Prices Guaranteed</h3>
            <p className="text-xl font-bold text-emerald-700 mb-4">These are the CHEAPEST tradelines on the internet!</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "✓ Credit Profile Friendly - Safe for all credit profiles",
                "✓ Best Value - Unbeatable prices for quality tradelines",
                "✓ Fast Processing - Quick turnaround times",
                "✓ Transparent - No hidden fees, everything upfront"
              ].map((item, idx) => (
                <div key={idx} className="bg-white p-3 rounded-lg shadow-sm">
                  <span className="text-lg text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-8">Get In Touch</h2>
          <p className="text-xl text-slate-300 mb-12">Have questions? We're here to help!</p>
          <div className="grid md:grid-cols-2 gap-6">
            <a href="mailto:trb.credit.king@gmail.com" className="bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-emerald-400 transition">
              <Mail className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              <h3 className="font-semibold text-white mb-2">Email Us</h3>
              <p className="text-emerald-400">trb.credit.king@gmail.com</p>
            </a>
            <a href="tel:410-863-2396" className="bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-emerald-400 transition">
              <Phone className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              <h3 className="font-semibold text-white mb-2">Call or Text</h3>
              <p className="text-emerald-400">410-863-2396</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ TRADELINE CARD ============
const TradelineCard = ({ tradeline }) => {
  const [showProof, setShowProof] = useState(false);
  
  const formatPrice = (price) => `$${price.toLocaleString()}`;
  const formatLimit = (limit) => `$${limit.toLocaleString()}`;
  const formatAge = (age) => {
    if (age < 1) return `${Math.round(age * 12)} months`;
    return `${age} ${age === 1 ? 'year' : 'years'}`;
  };

  // Calculate average rating
  const reviews = tradeline.reviews || [];
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;
  
  return (
    <>
      <div className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all hover:shadow-xl ${
        tradeline.isPrimary ? 'border-purple-400' : tradeline.noReport ? 'border-orange-300' : 'border-slate-200 hover:border-emerald-400'
      }`}>
        {tradeline.isPrimary && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 text-sm font-semibold">
            ⭐ PRIMARY ACCOUNT - PERMANENT
          </div>
        )}
        {tradeline.noReport && !tradeline.isPrimary && (
          <div className="bg-orange-500 text-white text-center py-2 text-sm font-semibold">
            ⚠️ Does NOT Report to Credit
          </div>
        )}
        {tradeline.verified && (
          <div className="bg-emerald-500 text-white text-center py-1 text-xs font-semibold">
            ✓ VERIFIED WITH PROOF
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="text-slate-500" size={18} />
            <span className="text-sm text-slate-500">{tradeline.accountType}</span>
          </div>
          <h3 className="text-lg font-bold mb-3 line-clamp-2">{tradeline.title}</h3>
          <div className="space-y-1 text-sm text-slate-600 mb-4">
            <p><span className="font-semibold">{tradeline.isPrimary ? 'Original Amount:' : 'Limit:'}</span> {formatLimit(tradeline.creditLimit)}</p>
            <p><span className="font-semibold">Age:</span> {formatAge(tradeline.age)}</p>
            {tradeline.isPrimary ? (
              <p className="text-emerald-600 font-semibold">✓ {tradeline.utilization}% Paid Off</p>
            ) : (
              <p><span className="font-semibold">Utilization:</span> {tradeline.utilization}%</p>
            )}
            {tradeline.postDate && <p><span className="font-semibold">Posts:</span> {tradeline.postDate}</p>}
            {!tradeline.isPrimary && (
              <p className="text-orange-600 font-medium">$75/month after first payment</p>
            )}
          </div>

          {/* Reviews Section */}
          {reviews.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-yellow-500 text-lg">{"★".repeat(Math.round(avgRating))}</span>
                <span className="font-semibold">{avgRating}</span>
                <span className="text-gray-500 text-sm">({reviews.length} review{reviews.length > 1 ? 's' : ''})</span>
              </div>
              {reviews.slice(0, 2).map((review, idx) => (
                <div key={idx} className="text-sm border-t border-yellow-200 pt-2 mt-2">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-400 text-xs">{"★".repeat(review.rating)}</span>
                    <span className="font-medium">{review.name}</span>
                    <span className="text-gray-400 text-xs">• {review.date}</span>
                  </div>
                  {review.comment && <p className="text-gray-600 text-xs mt-1">{review.comment}</p>}
                </div>
              ))}
              {reviews.length > 2 && (
                <p className="text-xs text-yellow-600 mt-2">+{reviews.length - 2} more review{reviews.length - 2 > 1 ? 's' : ''}</p>
              )}
            </div>
          )}
          
          {/* Proof Button */}
          {tradeline.images?.length > 0 && (
            <button
              onClick={() => setShowProof(true)}
              className="w-full mb-4 flex items-center justify-center gap-2 py-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition text-sm"
            >
              <CheckCircle size={16} />
              View Proof ({tradeline.images.length} files)
            </button>
          )}
          
          <div className="flex items-center justify-between">
            <div>
              <span className="text-2xl font-bold text-emerald-600">{formatPrice(tradeline.price)}</span>
              {tradeline.isPrimary && <span className="text-xs text-slate-500 block">One-time fee</span>}
            </div>
            <Link 
              to={`/checkout/${tradeline._id}`}
              className="bg-emerald-500 text-white px-5 py-2 rounded-lg hover:bg-emerald-600 transition text-sm font-semibold"
            >
              Select
            </Link>
          </div>
          
          {/* Slots */}
          <div className="mt-3 text-xs text-slate-500">
            {tradeline.slotsAvailable} of {tradeline.slotsTotal} slots available
          </div>
        </div>
      </div>
      
      {showProof && <ProofModal tradeline={tradeline} onClose={() => setShowProof(false)} />}
    </>
  );
};

// ============ PROOF MODAL ============
const ProofModal = ({ tradeline, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
        <h3 className="text-xl font-bold">Proof for {tradeline.title}</h3>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
          <X size={24} />
        </button>
      </div>
      <div className="p-6 space-y-6">
        {tradeline.images?.map((img, idx) => (
          <div key={idx} className="rounded-lg overflow-hidden border">
            {img.toLowerCase().includes('.mov') || img.toLowerCase().includes('.mp4') || img.toLowerCase().includes('video') ? (
              <video controls className="w-full" src={img}>
                Your browser does not support the video tag.
              </video>
            ) : (
              <img src={img} alt={`Proof ${idx + 1}`} className="w-full" />
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============ TRADELINES PAGE ============
const TradelinesPage = () => {
  const [tradelines, setTradelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchTradelines = async () => {
      try {
        await axios.post(`${API}/seed`).catch(() => {});
        const response = await axios.get(`${API}/tradelines`);
        setTradelines(response.data);
        // Track page view
        axios.post(`${API}/track-view`, { page: 'tradelines' }).catch(() => {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchTradelines();
  }, []);

  const filteredTradelines = filter === "all" 
    ? tradelines 
    : filter === "primary" 
    ? tradelines.filter(t => t.isPrimary)
    : tradelines.filter(t => !t.isPrimary);

  const primaryTradelines = filteredTradelines.filter(t => t.isPrimary);
  const regularTradelines = filteredTradelines.filter(t => !t.isPrimary);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Buy Tradelines - Authorized User & Primary Accounts"
        description="Browse our selection of premium tradelines for sale. Navy Federal, Discover, Amex authorized user tradelines and primary accounts. Boost your credit score fast with verified tradelines."
        keywords="buy tradelines, tradelines for sale, authorized user tradelines, primary accounts for sale, Navy Federal tradelines, Discover tradelines, Amex tradelines, credit building tradelines, AU tradelines, cheap tradelines"
        canonicalPath="/tradelines"
      />
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-4">Our Tradelines</h1>
        <p className="text-center text-gray-600 mb-8">Choose from our selection of premium tradelines</p>
        
        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <button 
            onClick={() => setFilter("all")}
            className={`px-6 py-2 rounded-full transition ${filter === "all" ? "bg-emerald-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
          >
            All
          </button>
          <button 
            onClick={() => setFilter("primary")}
            className={`px-6 py-2 rounded-full transition ${filter === "primary" ? "bg-emerald-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
          >
            Primary Accounts
          </button>
          <button 
            onClick={() => setFilter("tradeline")}
            className={`px-6 py-2 rounded-full transition ${filter === "tradeline" ? "bg-emerald-500 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
          >
            Tradelines
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading tradelines...</div>
        ) : (
          <>
            {/* Primary Accounts */}
            {primaryTradelines.length > 0 && (filter === "all" || filter === "primary") && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Star className="text-purple-500" /> Primary Accounts (Co-Borrower)
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {primaryTradelines.map(tradeline => (
                    <TradelineCard key={tradeline._id} tradeline={tradeline} />
                  ))}
                </div>
              </div>
            )}

            {/* Regular Tradelines */}
            {regularTradelines.length > 0 && (filter === "all" || filter === "tradeline") && (
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <CreditCard className="text-slate-600" /> Authorized User Tradelines
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {regularTradelines.map(tradeline => (
                    <TradelineCard key={tradeline._id} tradeline={tradeline} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============ CHECKOUT PAGE ============
const CheckoutPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tradeline, setTradeline] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [discountInput, setDiscountInput] = useState("");
  const [zoomedImage, setZoomedImage] = useState(null);
  
  // Get affiliate code from URL param, cookie, or localStorage
  const getAffiliateCode = () => {
    // First check URL param
    const urlCode = searchParams.get('ref');
    if (urlCode) return urlCode.toUpperCase();
    
    // Then check cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'affiliate_code') return value;
    }
    
    // Finally check localStorage
    const storedCode = localStorage.getItem('affiliate_code');
    const storedTimestamp = localStorage.getItem('affiliate_timestamp');
    if (storedCode && storedTimestamp) {
      // Check if within 30 days
      const daysDiff = (Date.now() - parseInt(storedTimestamp)) / (1000 * 60 * 60 * 24);
      if (daysDiff <= 30) return storedCode;
    }
    
    return "";
  };
  
  const affiliateCode = getAffiliateCode();
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerDob: "",
    customerSsn: "",
    shippingAddress: "",
    paymentMethod: "zelle"
  });
  const [showSsn, setShowSsn] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tradelineRes, settingsRes] = await Promise.all([
          axios.get(`${API}/tradelines/${id}`),
          axios.get(`${API}/settings`)
        ]);
        setTradeline(tradelineRes.data);
        setSettings(settingsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const applyDiscount = () => {
    if (settings?.discountEnabled && discountInput === settings?.discountCode) {
      setDiscountApplied(true);
      alert("Discount applied!");
    } else {
      alert("Invalid discount code");
    }
  };

  const calculateTotal = () => {
    if (!tradeline || !settings) return 0;
    let total = tradeline.price;
    
    // Apply discount
    if (discountApplied && settings.discountPercent > 0) {
      total = total - (total * settings.discountPercent / 100);
    }
    
    // Apply tax
    if (settings.taxEnabled && settings.taxRate > 0) {
      total = total + (total * settings.taxRate / 100);
    }
    
    // Apply CashApp fee
    if (formData.paymentMethod === "cashapp") {
      total = total + (settings.cashappFee || 7);
    }
    
    return total.toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const orderData = {
        ...formData,
        tradelineId: tradeline._id,
        tradelineTitle: tradeline.title,
        amount: parseFloat(calculateTotal()),
        affiliateCode: affiliateCode || null
      };
      await axios.post(`${API}/orders`, orderData);
      setSuccess(true);
    } catch (e) {
      console.error(e);
      alert("Error submitting order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!tradeline) return <div className="min-h-screen flex items-center justify-center">Tradeline not found</div>;

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-500" size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Order Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your order. Please complete your payment via {formData.paymentMethod === "zelle" ? "Zelle" : "Cash App"} and text us at <span className="font-semibold">410-863-2396</span> to confirm.
          </p>
          <div className="bg-emerald-50 p-4 rounded-lg mb-6">
            <p className="font-semibold text-emerald-800">Total: ${calculateTotal()}</p>
          </div>
          <Link to="/" className="bg-emerald-500 text-white px-6 py-3 rounded-lg inline-block hover:bg-emerald-600 transition">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {zoomedImage && <ImageZoomModal src={zoomedImage} onClose={() => setZoomedImage(null)} />}
      
      <div className="max-w-4xl mx-auto px-4">
        {/* Affiliate Banner */}
        {affiliateCode && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
            <span className="text-emerald-700">🎉 Shopping with affiliate code: <strong>{affiliateCode}</strong></span>
          </div>
        )}
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            {/* Product Images - Clickable to Zoom */}
            {tradeline.images?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">👆 Click images to zoom</p>
                <div className="grid grid-cols-2 gap-2">
                  {tradeline.images.slice(0, 4).map((img, idx) => (
                    <div 
                      key={idx} 
                      className="aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition"
                      onClick={() => setZoomedImage(img)}
                    >
                      {img.toLowerCase().includes('.mov') || img.toLowerCase().includes('.mp4') || img.toLowerCase().includes('video') ? (
                        <div className="w-full h-full flex items-center justify-center bg-slate-200">
                          <Play className="text-slate-500" size={32} />
                        </div>
                      ) : (
                        <img src={img} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-emerald-600 mt-2 text-center">✓ Verified proof images</p>
              </div>
            )}
            
            <div className="border-b pb-4 mb-4">
              <h3 className="font-semibold">{tradeline.title}</h3>
              <p className="text-gray-500">{tradeline.accountType}</p>
              {tradeline.isPrimary && (
                <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  ⭐ PRIMARY ACCOUNT - PERMANENT
                </span>
              )}
              <div className="mt-2 text-sm">
                <p>{tradeline.isPrimary ? 'Original Amount:' : 'Credit Limit:'} ${tradeline.creditLimit?.toLocaleString()}</p>
                <p>{tradeline.isPrimary ? `${tradeline.utilization}% Paid Off` : `Utilization: ${tradeline.utilization}%`}</p>
              </div>
            </div>
            
            {/* Price Breakdown */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Price</span>
                <span>${tradeline.price.toFixed(2)}</span>
              </div>
              
              {/* Discount */}
              {discountApplied && settings?.discountPercent > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({settings.discountPercent}%)</span>
                  <span>-${(tradeline.price * settings.discountPercent / 100).toFixed(2)}</span>
                </div>
              )}
              
              {/* Tax */}
              {settings?.taxEnabled && settings?.taxRate > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Tax ({settings.taxRate}%)</span>
                  <span>${((tradeline.price - (discountApplied ? tradeline.price * settings.discountPercent / 100 : 0)) * settings.taxRate / 100).toFixed(2)}</span>
                </div>
              )}
              
              {/* CashApp Fee */}
              {formData.paymentMethod === "cashapp" && (
                <div className="flex justify-between text-orange-600">
                  <span>CashApp Fee</span>
                  <span>${(settings?.cashappFee || 7).toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span className="text-emerald-600">${calculateTotal()}</span>
              </div>
            </div>
            
            {/* Discount Code Input */}
            {settings?.discountEnabled && !discountApplied && (
              <div className="mt-4 pt-4 border-t">
                <label className="block text-sm font-medium mb-2">Have a discount code?</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={applyDiscount}
                    className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
            {discountApplied && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                ✓ Discount code applied: {settings?.discountPercent}% off
              </div>
            )}
          </div>

          {/* Checkout Form */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Your Information</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.customerName}
                  onChange={e => setFormData({...formData, customerName: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.customerEmail}
                  onChange={e => setFormData({...formData, customerEmail: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.customerPhone}
                  onChange={e => setFormData({...formData, customerPhone: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                <input
                  type="date"
                  required
                  value={formData.customerDob}
                  onChange={e => setFormData({...formData, customerDob: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Social Security Number *</label>
                <div className="relative">
                  <input
                    type={showSsn ? "text" : "password"}
                    required
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    value={formData.customerSsn}
                    onChange={e => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length > 3) value = value.slice(0,3) + "-" + value.slice(3);
                      if (value.length > 6) value = value.slice(0,6) + "-" + value.slice(6);
                      setFormData({...formData, customerSsn: value});
                    }}
                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSsn(!showSsn)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSsn ? "Hide" : "Show"}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">🔒 Your SSN is encrypted and secure</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address *</label>
                <textarea
                  required
                  placeholder="Enter your full address (Street, City, State, ZIP)"
                  value={formData.shippingAddress}
                  onChange={e => setFormData({...formData, shippingAddress: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={3}
                />
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">⚠️ Important:</span> The address you provide will be used as the address on your credit report for the loans and tradelines you purchase. Please ensure it is accurate.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Payment Method *</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, paymentMethod: "zelle"})}
                    className={`p-4 border-2 rounded-lg transition ${formData.paymentMethod === "zelle" ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <span className="font-semibold">Zelle</span>
                    <span className="block text-sm text-gray-500">No extra fee</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, paymentMethod: "cashapp"})}
                    className={`p-4 border-2 rounded-lg transition ${formData.paymentMethod === "cashapp" ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    <span className="font-semibold">Cash App</span>
                    <span className="block text-sm text-gray-500">+${settings?.cashappFee || 7} fee</span>
                  </button>
                </div>
                
                {/* Payment QR Code Display */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  {formData.paymentMethod === "zelle" ? (
                    <div className="text-center">
                      <h4 className="font-semibold text-emerald-700 mb-2">Pay with Zelle</h4>
                      <div className="bg-white p-3 rounded-lg inline-block mb-2">
                        <img 
                          src={settings?.zelleQrCode || "https://customer-assets.emergentagent.com/job_login-helper-35/artifacts/rbweyfg6_IMG_0998.jpg"} 
                          alt="Zelle QR Code" 
                          className="w-48 h-48 mx-auto object-contain"
                        />
                      </div>
                      <p className="text-sm text-gray-600">Scan QR code to pay with Zelle</p>
                      <p className="font-semibold text-purple-600 mt-1 text-lg">{settings?.zellePhone || "667-218-8511"}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <h4 className="font-semibold text-emerald-700 mb-2">Pay with Cash App</h4>
                      <div className="bg-white p-3 rounded-lg inline-block mb-2">
                        <img 
                          src={settings?.cashappQrCode || "https://customer-assets.emergentagent.com/job_login-helper-35/artifacts/gtvve6n7_IMG_0996.jpg"} 
                          alt="CashApp QR Code" 
                          className="w-48 h-48 mx-auto object-contain"
                        />
                      </div>
                      <p className="text-sm text-gray-600">Scan QR code to pay with Cash App</p>
                      <p className="font-semibold text-green-600 mt-1 text-lg">{settings?.cashappTag || "$TRB443"}</p>
                      <p className="text-xs text-orange-600 mt-1">+${settings?.cashappFee || 7} processing fee included in total</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-3 text-center">After payment, text <span className="font-semibold">410-863-2396</span> to confirm</p>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-emerald-500 text-white py-4 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Order"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN LOGIN ============
const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        localStorage.setItem("adminToken", response.data.token);
        navigate("/admin/dashboard");
      }
    } catch (e) {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-center mb-6">Admin Login</h1>
        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4">{error}</div>}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full border rounded-lg px-4 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border rounded-lg px-4 py-2"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-500 text-white py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ ADMIN DASHBOARD ============
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [tradelines, setTradelines] = useState([]);
  const [settings, setSettings] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [helpQuestions, setHelpQuestions] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [ordersRes, tradelinesRes, settingsRes, sellersRes, helpRes, affiliatesRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/tradelines`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/sellers`),
        axios.get(`${API}/help`),
        axios.get(`${API}/affiliates`)
      ]);
      setOrders(ordersRes.data);
      setTradelines(tradelinesRes.data);
      setSettings(settingsRes.data);
      setSellers(sellersRes.data);
      setHelpQuestions(helpRes.data);
      setAffiliates(affiliatesRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  const primaryTradelines = tradelines.filter(t => t.isPrimary);
  const regularTradelines = tradelines.filter(t => !t.isPrimary);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Payment Reminder Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <DollarSign size={20} />
            <span className="font-medium">💳 Reminder: Website Monthly Payment Due</span>
          </div>
          <a 
            href="https://app.emergent.sh/billing" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-white text-orange-600 px-4 py-1.5 rounded-lg font-semibold hover:bg-orange-50 transition text-sm"
          >
            Pay Now →
          </a>
        </div>
      </div>

      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">TRB Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 px-4 py-2 rounded-lg transition"
            >
              <Eye size={18} /> Preview Website
            </a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-300 hover:text-white">
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            <button onClick={() => setActiveTab("analytics")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "analytics" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}>
              📊 Analytics
            </button>
            <button onClick={() => setActiveTab("orders")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "orders" ? "border-b-2 border-emerald-500 text-emerald-500" : "text-gray-500 hover:text-gray-700"}`}>
              Orders ({orders.length})
            </button>
            <button onClick={() => setActiveTab("primary")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "primary" ? "border-b-2 border-purple-500 text-purple-500" : "text-gray-500 hover:text-gray-700"}`}>
              ⭐ Primary ({primaryTradelines.length})
            </button>
            <button onClick={() => setActiveTab("tradelines")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "tradelines" ? "border-b-2 border-emerald-500 text-emerald-500" : "text-gray-500 hover:text-gray-700"}`}>
              Tradelines ({regularTradelines.length})
            </button>
            <button onClick={() => setActiveTab("sellers")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "sellers" ? "border-b-2 border-purple-500 text-purple-500" : "text-gray-500 hover:text-gray-700"}`}>
              🏪 Sellers ({sellers.length})
            </button>
            <button onClick={() => setActiveTab("help")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "help" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500 hover:text-gray-700"}`}>
              ❓ Help ({helpQuestions.filter(q => q.status === 'pending').length})
            </button>
            <button onClick={() => setActiveTab("affiliates")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "affiliates" ? "border-b-2 border-green-500 text-green-500" : "text-gray-500 hover:text-gray-700"}`}>
              🤝 Affiliates ({affiliates.length})
            </button>
            <button onClick={() => setActiveTab("content")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "content" ? "border-b-2 border-pink-500 text-pink-500" : "text-gray-500 hover:text-gray-700"}`}>
              ✏️ Edit Content
            </button>
            <button onClick={() => setActiveTab("settings")} className={`py-3 px-4 font-medium transition whitespace-nowrap text-sm ${activeTab === "settings" ? "border-b-2 border-orange-500 text-orange-500" : "text-gray-500 hover:text-gray-700"}`}>
              ⚙️ Settings
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === "analytics" ? (
          <AnalyticsTab />
        ) : activeTab === "orders" ? (
          <OrdersTab orders={orders} updateStatus={updateOrderStatus} deleteOrder={deleteOrder} />
        ) : activeTab === "primary" ? (
          <TradelinesTab tradelines={primaryTradelines} refresh={fetchData} tabTitle="Primary Loans Management" tabDesc="Manage your PRIMARY account loans (Co-Borrower)" />
        ) : activeTab === "tradelines" ? (
          <TradelinesTab tradelines={regularTradelines} refresh={fetchData} tabTitle="Tradelines Management" tabDesc="Manage authorized user tradelines" />
        ) : activeTab === "sellers" ? (
          <SellersTab sellers={sellers} refresh={fetchData} />
        ) : activeTab === "help" ? (
          <HelpTab questions={helpQuestions} refresh={fetchData} />
        ) : activeTab === "affiliates" ? (
          <AffiliatesTab affiliates={affiliates} refresh={fetchData} />
        ) : activeTab === "content" ? (
          <ContentEditorTab />
        ) : activeTab === "settings" ? (
          <SettingsTab settings={settings} refresh={fetchData} />
        ) : (
          <TradelinesTab tradelines={tradelines} refresh={fetchData} tabTitle="All Tradelines & Loans" tabDesc="Manage all tradelines and primary loans" />
        )}
      </div>
    </div>
  );
};

// ============ ANALYTICS TAB (Admin) ============
const AnalyticsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [pageViews, setPageViews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const [analyticsRes, viewsRes] = await Promise.all([
          axios.get(`${API}/analytics`),
          axios.get(`${API}/page-views`)
        ]);
        setAnalytics(analyticsRes.data);
        setPageViews(viewsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) return <div className="text-center py-12">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">📊 Analytics Dashboard</h2>
        <p className="text-gray-600">Track your business performance</p>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6">
          <p className="text-blue-100 text-sm">Total Orders</p>
          <p className="text-4xl font-bold">{analytics?.totalOrders || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-6">
          <p className="text-emerald-100 text-sm">Total Revenue</p>
          <p className="text-4xl font-bold">${(analytics?.totalRevenue || 0).toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6">
          <p className="text-purple-100 text-sm">Completed Orders</p>
          <p className="text-4xl font-bold">{analytics?.completedOrders || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-6">
          <p className="text-amber-100 text-sm">Pending Orders</p>
          <p className="text-4xl font-bold">{analytics?.pendingOrders || 0}</p>
        </div>
      </div>

      {/* Order Status & Revenue Chart */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Order Status */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4">Order Status Breakdown</h3>
          <div className="space-y-3">
            {['pending', 'paid', 'processing', 'completed'].map(status => (
              <div key={status} className="flex items-center gap-3">
                <div className="w-24 text-sm text-gray-600 capitalize">{status}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                  <div 
                    className={`h-full ${
                      status === 'pending' ? 'bg-yellow-400' :
                      status === 'paid' ? 'bg-green-400' :
                      status === 'processing' ? 'bg-blue-400' : 'bg-gray-400'
                    }`}
                    style={{width: `${analytics?.totalOrders > 0 ? ((analytics?.statusCounts?.[status] || 0) / analytics?.totalOrders * 100) : 0}%`}}
                  ></div>
                </div>
                <div className="w-12 text-right font-medium">{analytics?.statusCounts?.[status] || 0}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Revenue */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4">Revenue (Last 7 Days)</h3>
          <div className="flex items-end gap-2 h-48">
            {analytics?.dailyRevenue?.map((day, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-emerald-400 rounded-t"
                  style={{
                    height: `${Math.max(5, (day.revenue / (Math.max(...analytics.dailyRevenue.map(d => d.revenue)) || 1)) * 150)}px`
                  }}
                ></div>
                <p className="text-xs text-gray-500 mt-2">{day.date}</p>
                <p className="text-xs font-medium">${day.revenue.toFixed(0)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Tradelines & Affiliate Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Tradelines */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4">🏆 Top Selling Tradelines</h3>
          {analytics?.topTradelines?.length > 0 ? (
            <div className="space-y-3">
              {analytics.topTradelines.map((tl, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{tl.title}</p>
                    <p className="text-sm text-gray-500">{tl.sales} sales • ${tl.revenue.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No sales data yet</p>
          )}
        </div>

        {/* Affiliate Stats */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4">🤝 Affiliate Performance</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{analytics?.totalAffiliates || 0}</p>
              <p className="text-xs text-gray-500">Total Affiliates</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{analytics?.affiliateSales || 0}</p>
              <p className="text-xs text-gray-500">Affiliate Sales</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">${(analytics?.affiliateEarnings || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Commissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Page Views */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-4">👁️ Page Views (Last 7 Days)</h3>
        <div className="flex items-end gap-2 h-32">
          {pageViews.map((day, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-blue-400 rounded-t"
                style={{
                  height: `${Math.max(5, (day.views / (Math.max(...pageViews.map(d => d.views)) || 1)) * 100)}px`
                }}
              ></div>
              <p className="text-xs text-gray-500 mt-2">{day.date}</p>
              <p className="text-xs font-medium">{day.views}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ CONTENT EDITOR TAB (Admin) ============
const ContentEditorTab = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/content`);
        setContent(res.data);
      } catch (e) {
        console.error(e);
        // Set defaults if fetch fails
        setContent({
          heroTitle: "TRB Tradelines",
          heroSubtitle: "Your Trusted Partner in Building Credit Excellence",
          heroDescription: "Premium authorized user tradelines to help you achieve your credit goals. Fast, secure, and reliable service.",
          aboutTitle: "Why Choose TRB Tradelines?",
          secureTitle: "Secure & Protected",
          secureDesc: "Your personal information is encrypted and handled with the highest security standards.",
          resultsTitle: "Proven Results",
          resultsDesc: "High-quality tradelines with excellent payment history and low utilization.",
          transparencyTitle: "Full Transparency",
          transparencyDesc: "We screenshot the entire process and keep you updated via email.",
          contactPhone: "410-863-2396",
          contactEmail: "trb.credit.king@gmail.com",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content);
      alert("Content saved successfully! Refresh the website to see changes.");
    } catch (e) {
      console.error(e);
      alert("Error saving content");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading content...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">✏️ Edit Website Content</h2>
          <p className="text-gray-600">Update text and content displayed on your website</p>
        </div>
        <button
          onClick={saveContent}
          disabled={saving}
          className="bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
        >
          {saving ? "Saving..." : "💾 Save All Changes"}
        </button>
      </div>

      {/* Hero Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Star className="text-yellow-500" /> Hero Section
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Main Title</label>
            <input
              type="text"
              value={content?.heroTitle || ""}
              onChange={(e) => setContent({...content, heroTitle: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="TRB Tradelines"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              type="text"
              value={content?.heroSubtitle || ""}
              onChange={(e) => setContent({...content, heroSubtitle: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="Your Trusted Partner..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={content?.heroDescription || ""}
              onChange={(e) => setContent({...content, heroDescription: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              rows={3}
              placeholder="Premium authorized user tradelines..."
            />
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <CheckCircle className="text-emerald-500" /> "Why Choose Us" Section
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Section Title</label>
            <input
              type="text"
              value={content?.aboutTitle || ""}
              onChange={(e) => setContent({...content, aboutTitle: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
            />
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {/* Card 1 */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium mb-1">Card 1 Title</label>
              <input
                type="text"
                value={content?.secureTitle || ""}
                onChange={(e) => setContent({...content, secureTitle: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              />
              <label className="block text-sm font-medium mb-1">Card 1 Description</label>
              <textarea
                value={content?.secureDesc || ""}
                onChange={(e) => setContent({...content, secureDesc: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
              />
            </div>
            {/* Card 2 */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium mb-1">Card 2 Title</label>
              <input
                type="text"
                value={content?.resultsTitle || ""}
                onChange={(e) => setContent({...content, resultsTitle: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              />
              <label className="block text-sm font-medium mb-1">Card 2 Description</label>
              <textarea
                value={content?.resultsDesc || ""}
                onChange={(e) => setContent({...content, resultsDesc: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
              />
            </div>
            {/* Card 3 */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium mb-1">Card 3 Title</label>
              <input
                type="text"
                value={content?.transparencyTitle || ""}
                onChange={(e) => setContent({...content, transparencyTitle: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
              />
              <label className="block text-sm font-medium mb-1">Card 3 Description</label>
              <textarea
                value={content?.transparencyDesc || ""}
                onChange={(e) => setContent({...content, transparencyDesc: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Phone className="text-blue-500" /> Contact Information
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Phone Number</label>
            <input
              type="text"
              value={content?.contactPhone || ""}
              onChange={(e) => setContent({...content, contactPhone: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="410-863-2396"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email Address</label>
            <input
              type="email"
              value={content?.contactEmail || ""}
              onChange={(e) => setContent({...content, contactEmail: e.target.value})}
              className="w-full border rounded-lg px-4 py-2"
              placeholder="trb.credit.king@gmail.com"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveContent}
          disabled={saving}
          className="bg-emerald-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
        >
          {saving ? "Saving..." : "💾 Save All Changes"}
        </button>
      </div>
    </div>
  );
};

// ============ SETTINGS TAB ============
const SettingsTab = ({ settings, refresh }) => {
  const [form, setForm] = useState({
    // Tax
    taxRate: settings?.taxRate || 0,
    taxEnabled: settings?.taxEnabled || false,
    // Discount
    discountCode: settings?.discountCode || "",
    discountPercent: settings?.discountPercent || 0,
    discountEnabled: settings?.discountEnabled || false,
    // Affiliate
    affiliateCommission: settings?.affiliateCommission || 10,
    // Zelle
    zelleEnabled: settings?.zelleEnabled !== false,
    zellePhone: settings?.zellePhone || "667-218-8511",
    zelleQrCode: settings?.zelleQrCode || "",
    // CashApp
    cashappEnabled: settings?.cashappEnabled !== false,
    cashappTag: settings?.cashappTag || "$TRB443",
    cashappQrCode: settings?.cashappQrCode || "",
    cashappFee: settings?.cashappFee || 7,
    // Credit/Debit Card
    cardEnabled: settings?.cardEnabled || false,
    cardProcessor: settings?.cardProcessor || "",
    cardApiKey: settings?.cardApiKey || "",
    cardFee: settings?.cardFee || 2.9,
    // PayPal
    paypalEnabled: settings?.paypalEnabled || false,
    paypalEmail: settings?.paypalEmail || "",
    paypalClientId: settings?.paypalClientId || "",
    paypalFee: settings?.paypalFee || 2.9,
    // Klarna
    klarnaEnabled: settings?.klarnaEnabled || false,
    klarnaApiKey: settings?.klarnaApiKey || "",
    klarnaFee: settings?.klarnaFee || 0,
    // Email
    emailEnabled: settings?.emailEnabled !== false,
    sendgridApiKey: settings?.sendgridApiKey || "",
    senderEmail: settings?.senderEmail || "Trb.Credit.King@gmail.com"
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        taxRate: settings.taxRate || 0,
        taxEnabled: settings.taxEnabled || false,
        discountCode: settings.discountCode || "",
        discountPercent: settings.discountPercent || 0,
        discountEnabled: settings.discountEnabled || false,
        affiliateCommission: settings.affiliateCommission || 10,
        zelleEnabled: settings.zelleEnabled !== false,
        zellePhone: settings.zellePhone || "667-218-8511",
        zelleQrCode: settings.zelleQrCode || "",
        cashappEnabled: settings.cashappEnabled !== false,
        cashappTag: settings.cashappTag || "$TRB443",
        cashappQrCode: settings.cashappQrCode || "",
        cashappFee: settings.cashappFee || 7,
        cardEnabled: settings.cardEnabled || false,
        cardProcessor: settings.cardProcessor || "",
        cardApiKey: settings.cardApiKey || "",
        cardFee: settings.cardFee || 2.9,
        paypalEnabled: settings.paypalEnabled || false,
        paypalEmail: settings.paypalEmail || "",
        paypalClientId: settings.paypalClientId || "",
        paypalFee: settings.paypalFee || 2.9,
        klarnaEnabled: settings.klarnaEnabled || false,
        klarnaApiKey: settings.klarnaApiKey || "",
        klarnaFee: settings.klarnaFee || 0,
        // Email Settings
        emailEnabled: settings.emailEnabled !== false,
        sendgridApiKey: settings.sendgridApiKey || "",
        senderEmail: settings.senderEmail || "Trb.Credit.King@gmail.com"
      });
    }
  }, [settings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form);
      refresh();
      alert("Settings saved successfully!");
    } catch (e) {
      console.error(e);
      alert("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Settings</h2>
          <p className="text-gray-600">Manage taxes, discounts, payment methods, and affiliate settings</p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
        >
          {saving ? "Saving..." : "💾 Save All Settings"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Tax Settings */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <DollarSign size={20} className="text-emerald-500" /> Tax Settings
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.taxEnabled}
                onChange={(e) => setForm({...form, taxEnabled: e.target.checked})}
                className="w-5 h-5"
              />
              <span className="font-medium">Enable Tax</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Tax Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={form.taxRate}
                onChange={(e) => setForm({...form, taxRate: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-4 py-2"
                placeholder="6"
              />
            </div>
          </div>
        </div>

        {/* Discount Settings */}
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Star size={20} className="text-yellow-500" /> Discount Settings
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.discountEnabled}
                onChange={(e) => setForm({...form, discountEnabled: e.target.checked})}
                className="w-5 h-5"
              />
              <span className="font-medium">Enable Discount Code</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Discount Code</label>
              <input
                type="text"
                value={form.discountCode}
                onChange={(e) => setForm({...form, discountCode: e.target.value.toUpperCase()})}
                className="w-full border rounded-lg px-4 py-2"
                placeholder="SAVE10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Discount Percent (%)</label>
              <input
                type="number"
                value={form.discountPercent}
                onChange={(e) => setForm({...form, discountPercent: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-4 py-2"
                placeholder="0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Affiliate Commission Settings */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          🤝 Affiliate Commission Settings
        </h3>
        <div className="grid md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1 text-emerald-100">Commission Per Sale ($)</label>
            <input
              type="number"
              value={form.affiliateCommission}
              onChange={(e) => setForm({...form, affiliateCommission: parseFloat(e.target.value) || 0})}
              className="w-full border rounded-lg px-4 py-2 text-gray-900"
              placeholder="15"
            />
            <p className="text-xs text-emerald-100 mt-1">Amount paid to affiliates for each successful referral</p>
          </div>
          <div className="bg-white/20 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold">${form.affiliateCommission}</p>
            <p className="text-sm text-emerald-100">per sale</p>
          </div>
        </div>
      </div>

      {/* Active Payment Methods */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-2">💰 Payment Methods</h3>
        <p className="text-gray-500 text-sm mb-6">Toggle payment methods on/off for customers</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Zelle Settings */}
          <div className={`border-2 rounded-xl p-5 transition ${form.zelleEnabled ? 'border-purple-400 bg-purple-50' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-bold text-purple-600">💳 Zelle Payment</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className={`text-sm font-medium ${form.zelleEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.zelleEnabled ? 'Active' : 'Inactive'}
                </span>
                <input
                  type="checkbox"
                  checked={form.zelleEnabled}
                  onChange={(e) => setForm({...form, zelleEnabled: e.target.checked})}
                  className="w-5 h-5"
                />
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Zelle Phone Number</label>
                <input
                  type="text"
                  value={form.zellePhone}
                  onChange={(e) => setForm({...form, zellePhone: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="667-218-8511"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zelle QR Code URL</label>
                <input
                  type="url"
                  value={form.zelleQrCode}
                  onChange={(e) => setForm({...form, zelleQrCode: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 text-sm"
                  placeholder="https://..."
                />
                {form.zelleQrCode && (
                  <img src={form.zelleQrCode} alt="Zelle QR" className="mt-2 w-24 h-24 object-contain border rounded" />
                )}
              </div>
            </div>
          </div>

          {/* CashApp Settings */}
          <div className={`border-2 rounded-xl p-5 transition ${form.cashappEnabled ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-bold text-green-600">💵 Cash App Payment</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className={`text-sm font-medium ${form.cashappEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.cashappEnabled ? 'Active' : 'Inactive'}
                </span>
                <input
                  type="checkbox"
                  checked={form.cashappEnabled}
                  onChange={(e) => setForm({...form, cashappEnabled: e.target.checked})}
                  className="w-5 h-5"
                />
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">CashApp Tag</label>
                <input
                  type="text"
                  value={form.cashappTag}
                  onChange={(e) => setForm({...form, cashappTag: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="$TRB443"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CashApp Fee ($)</label>
                <input
                  type="number"
                  value={form.cashappFee}
                  onChange={(e) => setForm({...form, cashappFee: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded-lg px-4 py-2"
                  placeholder="7"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CashApp QR Code URL</label>
                <input
                  type="url"
                  value={form.cashappQrCode}
                  onChange={(e) => setForm({...form, cashappQrCode: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 text-sm"
                  placeholder="https://..."
                />
                {form.cashappQrCode && (
                  <img src={form.cashappQrCode} alt="CashApp QR" className="mt-2 w-24 h-24 object-contain border rounded" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Future Payment Options */}
      <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-6">
        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
          🔮 Future Payment Options
        </h3>
        <p className="text-gray-500 text-sm mb-6">Set up now, activate when ready - these will show to customers when enabled</p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* Credit/Debit Card */}
          <div className={`bg-white border-2 rounded-xl p-5 transition ${form.cardEnabled ? 'border-blue-400' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-bold text-blue-600">💳 Credit/Debit Card</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className={`text-xs font-medium ${form.cardEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.cardEnabled ? 'Active' : 'Inactive'}
                </span>
                <input
                  type="checkbox"
                  checked={form.cardEnabled}
                  onChange={(e) => setForm({...form, cardEnabled: e.target.checked})}
                  className="w-4 h-4"
                />
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Processor</label>
                <select
                  value={form.cardProcessor}
                  onChange={(e) => setForm({...form, cardProcessor: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select...</option>
                  <option value="stripe">Stripe</option>
                  <option value="square">Square</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">API Key</label>
                <input
                  type="password"
                  value={form.cardApiKey}
                  onChange={(e) => setForm({...form, cardApiKey: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="sk_live_..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Processing Fee (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.cardFee}
                  onChange={(e) => setForm({...form, cardFee: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="2.9"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Configure now, enable when you have processor set up</p>
          </div>

          {/* PayPal */}
          <div className={`bg-white border-2 rounded-xl p-5 transition ${form.paypalEnabled ? 'border-blue-400' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-bold text-blue-800">🅿️ PayPal</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className={`text-xs font-medium ${form.paypalEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.paypalEnabled ? 'Active' : 'Inactive'}
                </span>
                <input
                  type="checkbox"
                  checked={form.paypalEnabled}
                  onChange={(e) => setForm({...form, paypalEnabled: e.target.checked})}
                  className="w-4 h-4"
                />
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">PayPal Email</label>
                <input
                  type="email"
                  value={form.paypalEmail}
                  onChange={(e) => setForm({...form, paypalEmail: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="business@email.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Client ID</label>
                <input
                  type="password"
                  value={form.paypalClientId}
                  onChange={(e) => setForm({...form, paypalClientId: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="AZD..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">PayPal Fee (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.paypalFee}
                  onChange={(e) => setForm({...form, paypalFee: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="2.9"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Configure now, enable when you have PayPal business account</p>
          </div>

          {/* Klarna */}
          <div className={`bg-white border-2 rounded-xl p-5 transition ${form.klarnaEnabled ? 'border-pink-400' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-bold text-pink-600">🛒 Klarna (Buy Now Pay Later)</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className={`text-xs font-medium ${form.klarnaEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {form.klarnaEnabled ? 'Active' : 'Inactive'}
                </span>
                <input
                  type="checkbox"
                  checked={form.klarnaEnabled}
                  onChange={(e) => setForm({...form, klarnaEnabled: e.target.checked})}
                  className="w-4 h-4"
                />
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Klarna API Key</label>
                <input
                  type="password"
                  value={form.klarnaApiKey}
                  onChange={(e) => setForm({...form, klarnaApiKey: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="klarna_..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Fee (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.klarnaFee}
                  onChange={(e) => setForm({...form, klarnaFee: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Configure now, enable when you have Klarna merchant account</p>
          </div>
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Mail className="text-blue-500" /> Email Settings (SendGrid)
        </h3>
        <p className="text-gray-500 text-sm mb-4">Configure email notifications for affiliates and customers</p>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.emailEnabled !== false}
              onChange={(e) => setForm({...form, emailEnabled: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="font-medium">Enable Email Notifications</span>
          </label>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">SendGrid API Key *</label>
              <input
                type="password"
                value={form.sendgridApiKey || ""}
                onChange={(e) => setForm({...form, sendgridApiKey: e.target.value})}
                className="w-full border rounded-lg px-4 py-2"
                placeholder="SG.xxxxxx..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Get free API key at <a href="https://sendgrid.com" target="_blank" rel="noreferrer" className="text-blue-500 underline">sendgrid.com</a> (100 emails/day free)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sender Email</label>
              <input
                type="email"
                value={form.senderEmail || "Trb.Credit.King@gmail.com"}
                onChange={(e) => setForm({...form, senderEmail: e.target.value})}
                className="w-full border rounded-lg px-4 py-2"
                placeholder="Trb.Credit.King@gmail.com"
              />
              <p className="text-xs text-gray-500 mt-1">Must be verified in SendGrid</p>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4 text-sm">
            <p className="font-medium text-blue-800 mb-2">📧 Emails will be sent for:</p>
            <ul className="text-blue-700 space-y-1 ml-4 list-disc">
              <li>Affiliate welcome (when they sign up)</li>
              <li>Affiliate link email (when you click "Email Link")</li>
              <li>Customer invoice (after every purchase)</li>
              <li>Affiliate commission notification (when their referral buys)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="bg-emerald-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
        >
          {saving ? "Saving..." : "💾 Save All Settings"}
        </button>
      </div>
    </div>
  );
};

// ============ ORDERS TAB ============
const OrdersTab = ({ orders, updateStatus, deleteOrder }) => {
  const [showSsn, setShowSsn] = useState({});
  
  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    paid: "bg-green-100 text-green-800",
    processing: "bg-blue-100 text-blue-800",
    completed: "bg-gray-100 text-gray-800"
  };

  const toggleSsn = (orderId) => {
    setShowSsn(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const maskSsn = (ssn) => {
    if (!ssn) return "N/A";
    return ssn.replace(/(\d{3})-?(\d{2})-?(\d{4})/, "***-**-$3");
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Orders</h2>
      {orders.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">No orders yet</div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-lg">{order.tradelineTitle}</h3>
                  <p className="text-gray-500 text-sm">Order ID: {order._id?.slice(0, 8)}...</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status] || statusColors.pending}`}>
                  {order.status?.toUpperCase()}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><span className="font-medium">Customer:</span> {order.customerName}</p>
                  <p><span className="font-medium">Email:</span> {order.customerEmail}</p>
                  <p><span className="font-medium">Phone:</span> {order.customerPhone}</p>
                  <p><span className="font-medium">DOB:</span> {order.customerDob || "N/A"}</p>
                  <p className="flex items-center gap-2">
                    <span className="font-medium">SSN:</span> 
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                      {showSsn[order._id] ? order.customerSsn : maskSsn(order.customerSsn)}
                    </span>
                    <button 
                      onClick={() => toggleSsn(order._id)}
                      className="text-emerald-600 hover:text-emerald-700 text-xs font-medium"
                    >
                      {showSsn[order._id] ? "Hide" : "Show"}
                    </button>
                  </p>
                </div>
                <div>
                  <p><span className="font-medium">Address:</span> {order.shippingAddress}</p>
                  <p><span className="font-medium">Payment:</span> {order.paymentMethod}</p>
                  <p><span className="font-medium">Amount:</span> ${order.amount}</p>
                  <p><span className="font-medium">Date:</span> {new Date(order.createdAt).toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <select
                  value={order.status}
                  onChange={(e) => updateStatus(order._id, e.target.value)}
                  className="border rounded-lg px-4 py-2"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                </select>
                <button
                  onClick={() => deleteOrder(order._id)}
                  className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ TRADELINES TAB WITH FULL ADMIN CONTROL ============
const TradelinesTab = ({ tradelines, refresh, tabTitle = "Tradelines Management", tabDesc = "Add, edit, delete tradelines and upload proof" }) => {
  const [selectedTradeline, setSelectedTradeline] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, date: "", comment: "" });
  const [editingReview, setEditingReview] = useState(null);
  
  const emptyTradeline = {
    title: "",
    price: 0,
    creditLimit: 0,
    age: 0,
    utilization: 0,
    accountType: "",
    openDate: "",
    closingDate: "",
    postDate: "",
    description: "",
    images: [],
    featured: false,
    verified: false,
    isPrimary: false,
    noReport: false,
    active: true,
    slotsTotal: 1,
    slotsAvailable: 1,
    reviews: []
  };
  
  const [editForm, setEditForm] = useState(emptyTradeline);

  const openProofModal = (tradeline) => {
    setSelectedTradeline(tradeline);
    setImageUrl("");
    setShowProofModal(true);
  };

  const openEditModal = (tradeline) => {
    setSelectedTradeline(tradeline);
    setEditForm({
      title: tradeline.title || "",
      price: tradeline.price || 0,
      creditLimit: tradeline.creditLimit || 0,
      age: tradeline.age || 0,
      utilization: tradeline.utilization || 0,
      accountType: tradeline.accountType || "",
      openDate: tradeline.openDate || "",
      closingDate: tradeline.closingDate || "",
      postDate: tradeline.postDate || "",
      description: tradeline.description || "",
      featured: tradeline.featured || false,
      verified: tradeline.verified || false,
      isPrimary: tradeline.isPrimary || false,
      noReport: tradeline.noReport || false,
      active: tradeline.active !== false,
      slotsTotal: tradeline.slotsTotal || 1,
      slotsAvailable: tradeline.slotsAvailable || 1
    });
    setShowEditModal(true);
  };

  const openAddModal = () => {
    setEditForm(emptyTradeline);
    setShowAddModal(true);
  };

  const openReviewsModal = (tradeline) => {
    setSelectedTradeline(tradeline);
    setReviewForm({ name: "", rating: 5, date: new Date().toISOString().split('T')[0], comment: "" });
    setEditingReview(null);
    setShowReviewsModal(true);
  };

  const addReview = async () => {
    if (!reviewForm.name || !reviewForm.date) {
      alert("Please enter name and date");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tradelines/${selectedTradeline._id}/reviews`, reviewForm);
      refresh();
      setReviewForm({ name: "", rating: 5, date: new Date().toISOString().split('T')[0], comment: "" });
      alert("Review added!");
    } catch (e) {
      console.error(e);
      alert("Error adding review");
    } finally {
      setSaving(false);
    }
  };

  const updateReview = async () => {
    if (!reviewForm.name || !reviewForm.date) {
      alert("Please enter name and date");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/tradelines/${selectedTradeline._id}/reviews/${editingReview.id}`, reviewForm);
      refresh();
      setEditingReview(null);
      setReviewForm({ name: "", rating: 5, date: new Date().toISOString().split('T')[0], comment: "" });
      alert("Review updated!");
    } catch (e) {
      console.error(e);
      alert("Error updating review");
    } finally {
      setSaving(false);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/tradelines/${selectedTradeline._id}/reviews/${reviewId}`);
      refresh();
    } catch (e) {
      console.error(e);
      alert("Error deleting review");
    }
  };

  const startEditReview = (review) => {
    setEditingReview(review);
    setReviewForm({
      name: review.name,
      rating: review.rating,
      date: review.date,
      comment: review.comment || ""
    });
  };

  const cancelEditReview = () => {
    setEditingReview(null);
    setReviewForm({ name: "", rating: 5, date: new Date().toISOString().split('T')[0], comment: "" });
  };

  const addProof = async () => {
    if (!imageUrl) {
      alert("Please enter an image/video URL");
      return;
    }
    setUploading(true);
    try {
      const existingImages = selectedTradeline.images || [];
      await axios.put(`${API}/tradelines/${selectedTradeline._id}`, {
        images: [...existingImages, imageUrl]
      });
      refresh();
      setShowProofModal(false);
      alert("Proof added successfully!");
    } catch (e) {
      console.error(e);
      alert("Error adding proof");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (tradelineId, imageUrl, allImages) => {
    if (!window.confirm("Remove this image/video?")) return;
    try {
      const newImages = allImages.filter(img => img !== imageUrl);
      await axios.put(`${API}/tradelines/${tradelineId}`, { images: newImages });
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tradelines/${selectedTradeline._id}`, editForm);
      refresh();
      setShowEditModal(false);
      alert("Tradeline updated successfully!");
    } catch (e) {
      console.error(e);
      alert("Error updating tradeline");
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    if (!editForm.title || !editForm.price) {
      alert("Please fill in at least Title and Price");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tradelines`, editForm);
      refresh();
      setShowAddModal(false);
      alert("Tradeline added successfully!");
    } catch (e) {
      console.error(e);
      alert("Error adding tradeline");
    } finally {
      setSaving(false);
    }
  };

  const deleteTradeline = async (tradelineId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await axios.delete(`${API}/tradelines/${tradelineId}`);
      refresh();
      alert("Tradeline deleted!");
    } catch (e) {
      console.error(e);
      alert("Error deleting tradeline");
    }
  };

  const TradelineForm = ({ isNew }) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Title *</label>
          <input
            type="text"
            value={editForm.title}
            onChange={(e) => setEditForm({...editForm, title: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Navy Fed PRIMARY Account $20K"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Price ($) *</label>
          <input
            type="number"
            value={editForm.price}
            onChange={(e) => setEditForm({...editForm, price: parseFloat(e.target.value) || 0})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Credit Limit ($)</label>
          <input
            type="number"
            value={editForm.creditLimit}
            onChange={(e) => setEditForm({...editForm, creditLimit: parseInt(e.target.value) || 0})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Age (years)</label>
          <input
            type="number"
            step="0.1"
            value={editForm.age}
            onChange={(e) => setEditForm({...editForm, age: parseFloat(e.target.value) || 0})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{editForm.isPrimary ? 'Paid Off (%)' : 'Utilization (%)'}</label>
          <input
            type="number"
            value={editForm.utilization}
            onChange={(e) => setEditForm({...editForm, utilization: parseInt(e.target.value) || 0})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Account Type</label>
          <input
            type="text"
            value={editForm.accountType}
            onChange={(e) => setEditForm({...editForm, accountType: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Revolving Credit, PRIMARY - Installment Loan"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Post Date</label>
          <input
            type="text"
            value={editForm.postDate}
            onChange={(e) => setEditForm({...editForm, postDate: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="23rd of each month"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Open Date</label>
          <input
            type="text"
            value={editForm.openDate}
            onChange={(e) => setEditForm({...editForm, openDate: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="12/02/2025"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Closing Date</label>
          <input
            type="text"
            value={editForm.closingDate}
            onChange={(e) => setEditForm({...editForm, closingDate: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="7th"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Slots Total</label>
          <input
            type="number"
            value={editForm.slotsTotal}
            onChange={(e) => setEditForm({...editForm, slotsTotal: parseInt(e.target.value) || 1})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Slots Available</label>
          <input
            type="number"
            value={editForm.slotsAvailable}
            onChange={(e) => setEditForm({...editForm, slotsAvailable: parseInt(e.target.value) || 1})}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={editForm.description}
            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
            className="w-full border rounded-lg px-3 py-2"
            rows={4}
            placeholder="Full description of the tradeline..."
          />
        </div>
      </div>
      
      {/* Checkboxes */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editForm.isPrimary}
            onChange={(e) => setEditForm({...editForm, isPrimary: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Primary Account</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editForm.featured}
            onChange={(e) => setEditForm({...editForm, featured: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Featured</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editForm.verified}
            onChange={(e) => setEditForm({...editForm, verified: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Verified</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editForm.noReport}
            onChange={(e) => setEditForm({...editForm, noReport: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">No Report (AU only)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={editForm.active}
            onChange={(e) => setEditForm({...editForm, active: e.target.checked})}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Active</span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{tabTitle}</h2>
          <p className="text-gray-600">{tabDesc}</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-emerald-500 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-emerald-600 transition font-semibold"
        >
          + Add New
        </button>
      </div>
      
      <div className="space-y-4">
        {tradelines.map(tradeline => (
          <div key={tradeline._id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-lg">{tradeline.title}</h3>
                <p className="text-gray-500">{tradeline.accountType} | ${tradeline.creditLimit?.toLocaleString()} | <span className="text-emerald-600 font-semibold">${tradeline.price}</span></p>
                <div className="flex gap-2 mt-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${tradeline.isPrimary ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                    {tradeline.isPrimary ? 'PRIMARY' : 'TRADELINE'}
                  </span>
                  {tradeline.featured && <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Featured</span>}
                  {tradeline.verified && <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Verified</span>}
                  {tradeline.noReport && <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">No Report</span>}
                  {!tradeline.active && <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(tradeline)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => openProofModal(tradeline)}
                  className="bg-emerald-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-600 transition"
                >
                  <Upload size={18} /> Proof
                </button>
                <button
                  onClick={() => openReviewsModal(tradeline)}
                  className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-yellow-600 transition"
                >
                  <Star size={18} /> Reviews ({tradeline.reviews?.length || 0})
                </button>
                <button
                  onClick={() => deleteTradeline(tradeline._id, tradeline.title)}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mb-3">
              <span>Age: {tradeline.age} yrs</span> | <span>{tradeline.isPrimary ? `${tradeline.utilization}% Paid Off` : `Utilization: ${tradeline.utilization}%`}</span> | <span>Posts: {tradeline.postDate || 'N/A'}</span> | <span>Slots: {tradeline.slotsAvailable}/{tradeline.slotsTotal}</span>
            </div>
            
            {/* Proof Images */}
            <div className="mt-4 pt-4 border-t">
              <h4 className="font-medium mb-2 text-sm">Proof Files ({tradeline.images?.length || 0}):</h4>
              {tradeline.images?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tradeline.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      {img.toLowerCase().includes('.mov') || img.toLowerCase().includes('.mp4') || img.toLowerCase().includes('video') ? (
                        <div className="w-20 h-20 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Video className="text-slate-500" size={24} />
                        </div>
                      ) : (
                        <img src={img} alt={`Proof ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                      )}
                      <button
                        onClick={() => removeImage(tradeline._id, img, tradeline.images)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No proof files</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Proof Modal */}
      {showProofModal && selectedTradeline && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Add Proof</h3>
              <button onClick={() => setShowProofModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={24} />
              </button>
            </div>
            <p className="text-gray-600 mb-4">{selectedTradeline.title}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Upload Image or Video</label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    
                    setUploading(true);
                    try {
                      const formData = new FormData();
                      for (let i = 0; i < files.length; i++) {
                        formData.append('files', files[i]);
                      }
                      
                      const uploadRes = await axios.post(`${API}/upload-multiple`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      if (uploadRes.data.files && uploadRes.data.files.length > 0) {
                        const existingImages = selectedTradeline.images || [];
                        const newUrls = uploadRes.data.files.map(f => `${BACKEND_URL}${f.url}`);
                        await axios.put(`${API}/tradelines/${selectedTradeline._id}`, {
                          images: [...existingImages, ...newUrls]
                        });
                        refresh();
                        setShowProofModal(false);
                        alert(`${uploadRes.data.files.length} file(s) uploaded successfully!`);
                      }
                    } catch (err) {
                      console.error(err);
                      alert("Error uploading files");
                    } finally {
                      setUploading(false);
                    }
                  }}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 transition"
                />
                <p className="text-sm text-gray-500 mt-2">Supported: JPG, PNG, GIF, MP4, MOV (multiple files allowed)</p>
              </div>
              {uploading && (
                <div className="text-center py-4">
                  <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-gray-600 mt-2">Uploading...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Tradeline Modal */}
      {showEditModal && selectedTradeline && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Edit Tradeline</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={24} />
              </button>
            </div>
            <TradelineForm isNew={false} />
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Tradeline Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Add New Tradeline</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={24} />
              </button>
            </div>
            <TradelineForm isNew={true} />
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveNew}
                disabled={saving}
                className="flex-1 bg-emerald-500 text-white py-3 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
              >
                {saving ? "Adding..." : "Add Tradeline"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Modal */}
      {showReviewsModal && selectedTradeline && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold">⭐ Manage Reviews</h3>
                <p className="text-gray-600 text-sm">{selectedTradeline.title}</p>
              </div>
              <button onClick={() => setShowReviewsModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {/* Add/Edit Review Form */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h4 className="font-semibold mb-3">{editingReview ? "Edit Review" : "Add New Review"}</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="John D."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input
                    type="date"
                    value={reviewForm.date}
                    onChange={(e) => setReviewForm({...reviewForm, date: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Rating *</label>
                  <div className="flex gap-2 items-center">
                    {[1,2,3,4,5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className={`text-2xl ${star <= reviewForm.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      >
                        ★
                      </button>
                    ))}
                    <span className="ml-2 text-gray-600">({reviewForm.rating}/5)</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Comment (optional)</label>
                  <input
                    type="text"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Great service!"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                {editingReview ? (
                  <>
                    <button
                      onClick={updateReview}
                      disabled={saving}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Update Review"}
                    </button>
                    <button
                      onClick={cancelEditReview}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={addReview}
                    disabled={saving}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                  >
                    {saving ? "Adding..." : "Add Review"}
                  </button>
                )}
              </div>
            </div>

            {/* Existing Reviews */}
            <div>
              <h4 className="font-semibold mb-3">Existing Reviews ({selectedTradeline.reviews?.length || 0})</h4>
              {selectedTradeline.reviews?.length > 0 ? (
                <div className="space-y-3">
                  {selectedTradeline.reviews.map((review) => (
                    <div key={review.id} className="bg-white border rounded-lg p-4 flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{review.name}</span>
                          <span className="text-yellow-400">
                            {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm">{review.date}</p>
                        {review.comment && <p className="text-gray-600 mt-1">{review.comment}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditReview(review)}
                          className="text-blue-500 hover:text-blue-700 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteReview(review.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No reviews yet. Add one above!</p>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => setShowReviewsModal(false)}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ SELLERS TAB (Admin) ============
const SellersTab = ({ sellers, refresh }) => {
  const [zoomedImage, setZoomedImage] = useState(null);

  const updateStatus = async (sellerId, status) => {
    try {
      await axios.put(`${API}/sellers/${sellerId}/status?status=${status}`);
      refresh();
      alert(`Seller application ${status}!`);
    } catch (e) {
      console.error(e);
      alert("Error updating status");
    }
  };

  const deleteSeller = async (sellerId) => {
    if (!window.confirm("Delete this seller application?")) return;
    try {
      await axios.delete(`${API}/sellers/${sellerId}`);
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800"
  };

  return (
    <div className="space-y-6">
      {zoomedImage && <ImageZoomModal src={zoomedImage} onClose={() => setZoomedImage(null)} />}
      
      <div>
        <h2 className="text-2xl font-bold">🏪 Seller Applications</h2>
        <p className="text-gray-600">Review and manage tradeline seller applications</p>
      </div>

      {sellers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No seller applications yet
        </div>
      ) : (
        <div className="space-y-4">
          {sellers.map(seller => (
            <div key={seller._id} className="bg-white rounded-xl shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">{seller.name}</h3>
                  <p className="text-gray-600">{seller.email} • {seller.phone}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[seller.status] || statusColors.pending}`}>
                  {seller.status?.toUpperCase() || 'PENDING'}
                </span>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500">Bank</p>
                  <p className="font-semibold">{seller.bankName}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500">Credit Limit</p>
                  <p className="font-semibold">${seller.creditLimit?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500">Utilization</p>
                  <p className="font-semibold">{seller.utilization}%</p>
                </div>
              </div>

              {/* Proof Images */}
              {seller.proofImages?.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Proof Images:</p>
                  <div className="flex gap-2 flex-wrap">
                    {seller.proofImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Proof ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80"
                        onClick={() => setZoomedImage(img)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Proof Video */}
              {seller.proofVideo && (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Proof Video:</p>
                  <button
                    onClick={() => setZoomedImage(seller.proofVideo)}
                    className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
                  >
                    <Play size={16} /> View Video
                  </button>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                {seller.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateStatus(seller._id, 'approved')}
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => updateStatus(seller._id, 'rejected')}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 text-sm"
                    >
                      ✗ Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => deleteSeller(seller._id)}
                  className="ml-auto text-red-500 hover:text-red-700"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ HELP TAB (Admin) ============
const HelpTab = ({ questions, refresh }) => {
  const [replyText, setReplyText] = useState({});

  const sendReply = async (questionId) => {
    const reply = replyText[questionId];
    if (!reply?.trim()) {
      alert("Please enter a reply");
      return;
    }
    try {
      await axios.put(`${API}/help/${questionId}/reply?reply=${encodeURIComponent(reply)}`);
      setReplyText({ ...replyText, [questionId]: "" });
      refresh();
      alert("Reply sent!");
    } catch (e) {
      console.error(e);
      alert("Error sending reply");
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/help/${questionId}`);
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">❓ Help Questions</h2>
        <p className="text-gray-600">Respond to customer questions and inquiries</p>
      </div>

      {questions.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No questions yet
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map(q => (
            <div key={q._id} className={`bg-white rounded-xl shadow p-6 ${q.status === 'pending' ? 'border-l-4 border-yellow-400' : ''}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold">{q.name}</h3>
                  <p className="text-gray-600 text-sm">{q.email} {q.phone && `• ${q.phone}`}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${q.status === 'replied' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {q.status === 'replied' ? 'REPLIED' : 'PENDING'}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700">{q.question}</p>
              </div>

              {q.reply && (
                <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                  <p className="text-xs text-emerald-600 mb-1 font-medium">Your Reply:</p>
                  <p className="text-emerald-800">{q.reply}</p>
                </div>
              )}

              {q.status !== 'replied' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText[q._id] || ""}
                    onChange={(e) => setReplyText({ ...replyText, [q._id]: e.target.value })}
                    placeholder="Type your reply..."
                    className="flex-1 border rounded-lg px-4 py-2"
                  />
                  <button
                    onClick={() => sendReply(q._id)}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 flex items-center gap-2"
                  >
                    <Send size={16} /> Reply
                  </button>
                </div>
              )}

              <div className="flex justify-end mt-4 pt-4 border-t">
                <button onClick={() => deleteQuestion(q._id)} className="text-red-500 hover:text-red-700">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ AFFILIATES TAB (Admin) ============
const AffiliatesTab = ({ affiliates, refresh }) => {
  const [settings, setSettings] = useState(null);
  const [commissionRate, setCommissionRate] = useState(10);
  const [commissionType, setCommissionType] = useState('flat');
  const [savingCommission, setSavingCommission] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [sendingEmail, setSendingEmail] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
        setCommissionRate(res.data.affiliateCommission || 10);
        setCommissionType(res.data.affiliateCommissionType || 'flat');
      } catch (e) {
        console.error(e);
      }
    };
    fetchSettings();
  }, []);

  const saveCommissionSettings = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${API}/settings`, {
        ...settings,
        affiliateCommission: commissionRate,
        affiliateCommissionType: commissionType
      });
      alert("Commission settings saved!");
    } catch (e) {
      console.error(e);
      alert("Error saving settings");
    } finally {
      setSavingCommission(false);
    }
  };

  const updateStatus = async (affiliateId, status) => {
    try {
      await axios.put(`${API}/affiliates/${affiliateId}/status?status=${status}`);
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteAffiliate = async (affiliateId) => {
    if (!window.confirm("Delete this affiliate?")) return;
    try {
      await axios.delete(`${API}/affiliates/${affiliateId}`);
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  const sendLinkEmail = async (affiliateId) => {
    setSendingEmail(prev => ({...prev, [affiliateId]: true}));
    try {
      const res = await axios.post(`${API}/affiliates/${affiliateId}/send-link-email`);
      alert(res.data.message || "Email sent!");
    } catch (e) {
      console.error(e);
      alert("Error sending email. Check email settings in Settings tab.");
    } finally {
      setSendingEmail(prev => ({...prev, [affiliateId]: false}));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const totalSales = affiliates.reduce((sum, a) => sum + (a.totalSales || 0), 0);
  const totalEarnings = affiliates.reduce((sum, a) => sum + (a.totalEarnings || 0), 0);
  const totalClicks = affiliates.reduce((sum, a) => sum + (a.totalClicks || 0), 0);
  const totalRevenue = affiliates.reduce((sum, a) => sum + (a.totalRevenue || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">🤝 Affiliate Management</h2>
        <p className="text-gray-600">Manage affiliates, track commissions, and send emails</p>
      </div>

      {/* Commission Settings */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <DollarSign size={20} /> Commission Settings
        </h3>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1 text-emerald-100">Commission Type</label>
            <select
              value={commissionType}
              onChange={(e) => setCommissionType(e.target.value)}
              className="w-full border rounded-lg px-4 py-2 text-gray-900"
            >
              <option value="flat">Flat Amount ($)</option>
              <option value="percent">Percentage (%)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-emerald-100">
              {commissionType === 'flat' ? 'Amount per Sale ($)' : 'Percentage of Sale (%)'}
            </label>
            <input
              type="number"
              step={commissionType === 'flat' ? '1' : '0.5'}
              value={commissionRate}
              onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
              className="w-full border rounded-lg px-4 py-2 text-gray-900"
            />
          </div>
          <button
            onClick={saveCommissionSettings}
            disabled={savingCommission}
            className="bg-white text-emerald-600 px-6 py-2 rounded-lg font-semibold hover:bg-emerald-50 transition disabled:opacity-50"
          >
            {savingCommission ? "Saving..." : "Save Settings"}
          </button>
        </div>
        <p className="mt-3 text-emerald-100 text-sm">
          Current: Affiliates earn {commissionType === 'flat' ? `$${commissionRate}` : `${commissionRate}%`} per sale
        </p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-gray-500 text-sm">Total Affiliates</p>
          <p className="text-3xl font-bold text-emerald-600">{affiliates.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-gray-500 text-sm">Total Clicks</p>
          <p className="text-3xl font-bold text-blue-600">{totalClicks}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-gray-500 text-sm">Total Sales</p>
          <p className="text-3xl font-bold text-green-600">{totalSales}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-gray-500 text-sm">Total Revenue</p>
          <p className="text-3xl font-bold text-indigo-600">${totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <p className="text-gray-500 text-sm">Commissions Owed</p>
          <p className="text-3xl font-bold text-purple-600">${totalEarnings.toFixed(2)}</p>
        </div>
      </div>

      {/* Affiliates List */}
      {affiliates.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          No affiliates yet
        </div>
      ) : (
        <div className="space-y-4">
          {affiliates.map(affiliate => (
            <div key={affiliate._id} className="bg-white rounded-xl shadow p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold">{affiliate.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${affiliate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {affiliate.status?.toUpperCase() || 'ACTIVE'}
                    </span>
                  </div>
                  <p className="text-gray-500">{affiliate.email}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => sendLinkEmail(affiliate._id)}
                    disabled={sendingEmail[affiliate._id]}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    <Mail size={16} /> {sendingEmail[affiliate._id] ? "Sending..." : "Email Link"}
                  </button>
                  <button
                    onClick={() => setSelectedAffiliate(selectedAffiliate?._id === affiliate._id ? null : affiliate)}
                    className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 text-sm"
                  >
                    {selectedAffiliate?._id === affiliate._id ? "Hide Details" : "View Details"}
                  </button>
                  {affiliate.status === 'active' ? (
                    <button onClick={() => updateStatus(affiliate._id, 'inactive')} className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm">
                      Deactivate
                    </button>
                  ) : (
                    <button onClick={() => updateStatus(affiliate._id, 'active')} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm">
                      Activate
                    </button>
                  )}
                  <button onClick={() => deleteAffiliate(affiliate._id)} className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Affiliate Link */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Affiliate Link:</span>
                  <code className="flex-1 bg-white border rounded px-3 py-2 text-emerald-600 font-mono text-sm">
                    https://www.trbcreditking.com/{affiliate.code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`https://www.trbcreditking.com/${affiliate.code}`)}
                    className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Stats Row */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Code</p>
                  <p className="font-bold text-blue-600">{affiliate.code}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Clicks</p>
                  <p className="font-bold">{affiliate.totalClicks || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Orders</p>
                  <p className="font-bold text-green-600">{affiliate.totalSales || 0}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Revenue</p>
                  <p className="font-bold text-indigo-600">${(affiliate.totalRevenue || 0).toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Commissions</p>
                  <p className="font-bold text-purple-600">${(affiliate.totalEarnings || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Commission History (Expanded) */}
              {selectedAffiliate?._id === affiliate._id && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-semibold mb-3">💰 Commission History</h4>
                  {affiliate.commissionHistory?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {affiliate.commissionHistory.map((comm, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
                          <div>
                            <p className="font-medium">{comm.productName}</p>
                            <p className="text-gray-500 text-xs">{comm.date?.slice(0, 10)} • Order #{comm.orderId?.slice(0, 8)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">+${comm.amount?.toFixed(2)}</p>
                            <p className="text-gray-400 text-xs">from ${comm.orderTotal?.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No commission history yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ BECOME A SELLER PAGE ============
const BecomeSellerPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    bankName: "",
    creditLimit: "",
    utilization: "",
    proofImages: [],
    proofVideo: ""
  });

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const uploadRes = await axios.post(`${API}/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.files && uploadRes.data.files.length > 0) {
        const newUrls = uploadRes.data.files.map(f => `${BACKEND_URL}${f.url}`);
        setForm(prev => ({
          ...prev,
          proofImages: [...prev.proofImages, ...newUrls]
        }));
      }
    } catch (err) {
      console.error(err);
      alert("Error uploading images");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.url) {
        setForm(prev => ({
          ...prev,
          proofVideo: `${BACKEND_URL}${uploadRes.data.url}`
        }));
      }
    } catch (err) {
      console.error(err);
      alert("Error uploading video");
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeImage = (idx) => {
    setForm(prev => ({
      ...prev,
      proofImages: prev.proofImages.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate utilization
    if (parseInt(form.utilization) > 10) {
      alert("Utilization must be 10% or under to qualify");
      return;
    }
    
    // Validate proof
    if (form.proofImages.length < 3) {
      alert("Please upload at least 3 proof images");
      return;
    }
    if (!form.proofVideo) {
      alert("Please upload a proof video");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/sellers`, {
        ...form,
        creditLimit: parseInt(form.creditLimit) || 0,
        utilization: parseInt(form.utilization) || 0,
      });
      setSuccess(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Error submitting application");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-500" size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">We'll review your application and contact you soon.</p>
          <Link to="/" className="bg-emerald-500 text-white px-6 py-3 rounded-lg inline-block hover:bg-emerald-600 transition">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Become a Tradeline Seller - Earn Money with Your Credit"
        description="Sell your tradelines and earn extra income. If you have credit cards with high limits and low utilization, become a tradeline seller with TRB Credit King."
        keywords="sell tradelines, tradeline seller, become tradeline seller, sell authorized user spots, tradeline business, earn money with credit cards"
        canonicalPath="/become-seller"
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Become a Tradeline Seller</h1>
          <p className="text-xl text-gray-600">Join our network and earn by selling tradelines</p>
        </div>

        {/* Requirements */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Star className="text-purple-500" /> Requirements to Qualify
          </h2>
          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <CheckCircle className="text-purple-500 flex-shrink-0" size={24} />
              <span className="text-lg"><strong>Utilization under 10%</strong> - Your credit card utilization must be 10% or below</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="text-purple-500 flex-shrink-0" size={24} />
              <span className="text-lg"><strong>3 Proof Images</strong> - Screenshots showing your account details</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="text-purple-500 flex-shrink-0" size={24} />
              <span className="text-lg"><strong>1 Proof Video</strong> - Screen recording showing your account</span>
            </li>
          </ul>
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Seller Application Form</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bank Name *</label>
                <input type="text" required value={form.bankName} onChange={e => setForm({...form, bankName: e.target.value})} className="w-full border rounded-lg px-4 py-2" placeholder="Navy Federal, Chase, etc." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Credit Limit ($) *</label>
                <input type="number" required value={form.creditLimit} onChange={e => setForm({...form, creditLimit: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Current Utilization (%) *</label>
                <input type="number" required max="10" value={form.utilization} onChange={e => setForm({...form, utilization: e.target.value})} className="w-full border rounded-lg px-4 py-2" placeholder="Must be 10 or under" />
                <p className="text-xs text-orange-600 mt-1">⚠️ Must be 10% or under to qualify</p>
              </div>
            </div>

            {/* Proof Images */}
            <div>
              <label className="block text-sm font-medium mb-2">Proof Images (3+ Required) *</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-purple-400 transition"
                disabled={uploadingFiles}
              />
              <p className="text-xs text-gray-500 mt-1">Upload screenshots showing your account details</p>
              
              {/* Preview uploaded images */}
              {form.proofImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.proofImages.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt={`Proof ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-emerald-600 mt-1">✓ {form.proofImages.length}/3 images uploaded</p>
            </div>

            {/* Proof Video */}
            <div>
              <label className="block text-sm font-medium mb-1">Proof Video *</label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-purple-400 transition"
                disabled={uploadingFiles}
              />
              <p className="text-xs text-gray-500 mt-1">Upload a screen recording showing your account</p>
              {form.proofVideo && (
                <p className="text-xs text-emerald-600 mt-1">✓ Video uploaded</p>
              )}
            </div>

            {uploadingFiles && (
              <div className="text-center py-2">
                <div className="animate-spin w-6 h-6 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-sm text-gray-600 mt-1">Uploading...</p>
              </div>
            )}

            <button type="submit" disabled={submitting} className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50">
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ HELP PAGE ============
const HelpPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    question: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/help`, form);
      setSuccess(true);
    } catch (e) {
      alert("Error submitting question");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-500" size={40} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Question Submitted!</h2>
          <p className="text-gray-600 mb-6">We'll reply to your email or phone soon.</p>
          <Link to="/" className="bg-emerald-500 text-white px-6 py-3 rounded-lg inline-block hover:bg-emerald-600 transition">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Help & Support - Contact TRB Credit King"
        description="Have questions about tradelines or your order? Contact TRB Credit King for help. Call 410-863-2396 or email trb.credit.king@gmail.com."
        keywords="tradelines help, tradeline questions, credit help, TRB Credit King contact, tradeline support"
        canonicalPath="/help"
      />
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-12">
          <HelpCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Help & Support</h1>
          <p className="text-xl text-gray-600">Have a question? We're here to help!</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Your Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email * (for reply)</label>
              <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone (optional)</label>
              <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Your Question *</label>
              <textarea required rows={5} value={form.question} onChange={e => setForm({...form, question: e.target.value})} className="w-full border rounded-lg px-4 py-2" placeholder="Type your question here..." />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-emerald-500 text-white py-4 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50">
              {submitting ? "Sending..." : "Send Question"}
            </button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <a href="tel:410-863-2396" className="bg-white rounded-xl shadow p-6 text-center hover:shadow-lg transition">
            <Phone className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold">Call or Text</p>
            <p className="text-emerald-600">410-863-2396</p>
          </a>
          <a href="mailto:trb.credit.king@gmail.com" className="bg-white rounded-xl shadow p-6 text-center hover:shadow-lg transition">
            <Mail className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold">Email</p>
            <p className="text-emerald-600">trb.credit.king@gmail.com</p>
          </a>
        </div>
      </div>
    </div>
  );
};

// ============ AFFILIATES PAGE ============
const AffiliatesPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/affiliates`, form);
      setAffiliate({ code: res.data.code, name: form.name });
    } catch (e) {
      alert(e.response?.data?.detail || "Error creating affiliate account");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/tradelines?ref=${code}`;
    navigator.clipboard.writeText(link);
    alert("Affiliate link copied!");
  };

  if (affiliate) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-emerald-500" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Welcome, {affiliate.name}!</h2>
            <p className="text-gray-600 mb-6">Your affiliate account is now active</p>
            
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Affiliate Code:</p>
              <p className="text-3xl font-bold text-emerald-600">{affiliate.code}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Affiliate Link:</p>
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  readOnly 
                  value={`https://www.trbcreditking.com/${affiliate.code}`} 
                  className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white"
                />
                <button onClick={() => copyLink(affiliate.code)} className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600">
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-left">
              <h3 className="font-semibold text-yellow-800 mb-2">💰 How it works:</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Share your link with friends & family</li>
                <li>• You earn <strong>$10</strong> for every purchase made through your link</li>
                <li>• Contact us to collect your earnings</li>
              </ul>
            </div>

            <Link to="/tradelines" className="mt-6 inline-block bg-emerald-500 text-white px-6 py-3 rounded-lg hover:bg-emerald-600 transition">
              Browse Tradelines
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Affiliate Program - Earn Money Referring Tradelines"
        description="Join TRB Credit King's affiliate program and earn money for every tradeline sale. Get your unique referral link and start earning commissions today."
        keywords="tradeline affiliate program, tradeline referral, earn money tradelines, affiliate program, referral commission"
        canonicalPath="/affiliates"
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <Users className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Affiliate Program</h1>
          <p className="text-xl text-gray-600">Earn $10 for every sale through your link!</p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="text-emerald-500" size={32} />
            </div>
            <h3 className="font-bold text-lg mb-2">$10 Per Sale</h3>
            <p className="text-gray-600">Earn $10 commission for every purchase made through your affiliate link</p>
          </div>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LinkIcon className="text-emerald-500" size={32} />
            </div>
            <h3 className="font-bold text-lg mb-2">Unique Link</h3>
            <p className="text-gray-600">Get your own unique affiliate link to share with friends and family</p>
          </div>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="text-emerald-500" size={32} />
            </div>
            <h3 className="font-bold text-lg mb-2">Track Earnings</h3>
            <p className="text-gray-600">We track all sales from your link and your earnings</p>
          </div>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">Become an Affiliate</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone *</label>
              <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full border rounded-lg px-4 py-2" />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-emerald-500 text-white py-4 rounded-lg font-semibold hover:bg-emerald-600 transition disabled:opacity-50">
              {submitting ? "Creating..." : "Get My Affiliate Link"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============ AFFILIATE LINK HANDLER ============
const AffiliateLinkHandler = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const trackAndRedirect = async () => {
      try {
        // Track the click
        await axios.post(`${API}/affiliates/track-click/${code}`);
        
        // Store affiliate code in cookie (30 days)
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 30);
        document.cookie = `affiliate_code=${code.toUpperCase()}; expires=${expiryDate.toUTCString()}; path=/`;
        
        // Store in localStorage as backup
        localStorage.setItem('affiliate_code', code.toUpperCase());
        localStorage.setItem('affiliate_timestamp', Date.now().toString());
      } catch (e) {
        console.error('Error tracking affiliate:', e);
      }
      
      // Redirect to tradelines page
      navigate('/tradelines');
    };
    
    trackAndRedirect();
  }, [code, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
            <Route path="/tradelines" element={<><Navbar /><TradelinesPage /><Footer /></>} />
            <Route path="/checkout/:id" element={<><Navbar /><CheckoutPage /><Footer /></>} />
            <Route path="/become-seller" element={<><Navbar /><BecomeSellerPage /><Footer /></>} />
            <Route path="/help" element={<><Navbar /><HelpPage /><Footer /></>} />
            <Route path="/affiliates" element={<><Navbar /><AffiliatesPage /><Footer /></>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/orders" element={<AdminDashboard />} />
            {/* Affiliate link handler - must be last to catch /{code} */}
            <Route path="/:code" element={<AffiliateLinkHandler />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
